import React, { CSSProperties } from "react";
import { SocialLink } from "./types";

interface SocialLinksExportProps {
  links?: SocialLink[];
  iconSize?: number;
  gap?: number;
  alignment?: "left" | "center" | "right";
  showLabels?: boolean;
  style?: "icons" | "buttons" | "minimal";
}

// PNG icon paths
const socialIconImages: Record<string, string> = {
  facebook: "/static/images/social/facebook.png",
  instagram: "/static/images/social/ig.png",
  line: "/static/images/social/line.png",
  linkedin: "/static/images/social/linked.png",
  tiktok: "/static/images/social/tiktok.png",
};

// Platform data for colors and labels (matching Editor values)
const platformData: Record<string, { color: string; label: string }> = {
  facebook: { color: "#1877F2", label: "Facebook" },
  instagram: { color: "#E4405F", label: "Instagram" },
  twitter: { color: "#000000", label: "X" },
  youtube: { color: "#FF0000", label: "YouTube" },
  line: { color: "#00B900", label: "LINE" },
  linkedin: { color: "#0A66C2", label: "LinkedIn" },
  tiktok: { color: "#000000", label: "TikTok" },
  whatsapp: { color: "#25D366", label: "WhatsApp" },
  email: { color: "#EA4335", label: "Email" },
  phone: { color: "#34A853", label: "Phone" },
};

const renderSocialIcon = (platform: string, size: number): React.ReactNode => {
  const iconSize = size * 0.6;

  if (socialIconImages[platform]) {
    return (
      <img
        src={socialIconImages[platform]}
        alt={platform}
        style={{ width: iconSize, height: iconSize, objectFit: "contain" }}
      />
    );
  }

  // SVG fallbacks
  switch (platform) {
    case "whatsapp":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      );
    case "youtube":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      );
    case "twitter":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "email":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      );
    case "phone":
      return (
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
        </svg>
      );
    default:
      return <span style={{ fontSize: iconSize * 0.8 }}>🔗</span>;
  }
};

export const SocialLinksExport: React.FC<SocialLinksExportProps> = ({
  links = [
    { platform: "facebook", url: "#" },
    { platform: "instagram", url: "#" },
    { platform: "line", url: "#" },
  ],
  iconSize = 40,
  gap = 12,
  alignment = "center",
  showLabels = false,
  style = "icons",
}) => {
  const safeLinks = Array.isArray(links) ? links : [];

  const containerStyle: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: `${gap}px`,
    justifyContent: alignment === "center" ? "center" : alignment === "right" ? "flex-end" : "flex-start",
    padding: "16px 0",
  };

  return (
    <div style={containerStyle}>
      {safeLinks.map((link, index) => {
        const platform = platformData[link.platform] || { color: "#6b7280", label: link.platform };
        const hasPngIcon = !!socialIconImages[link.platform];

        // Buttons style - rectangular buttons with background
        if (style === "buttons") {
          return (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 16px",
                borderRadius: "8px",
                color: hasPngIcon ? "#333" : "#ffffff",
                textDecoration: "none",
                backgroundColor: hasPngIcon ? "#f3f4f6" : platform.color,
                transition: "opacity 0.2s",
              }}
            >
              {renderSocialIcon(link.platform, iconSize)}
              {showLabels && <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{platform.label}</span>}
            </a>
          );
        }

        // Minimal style - just icons without background
        if (style === "minimal") {
          return (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
              >
                {renderSocialIcon(link.platform, iconSize)}
              </a>
              {showLabels && <span style={{ fontSize: "0.875rem", color: "#4b5563" }}>{platform.label}</span>}
            </div>
          );
        }

        // Default 'icons' style - circular icons
        return (
          <div key={index} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "flex", textDecoration: "none", transition: "transform 0.2s" }}
            >
              {hasPngIcon ? (
                // PNG images already have circular backgrounds with colors
                renderSocialIcon(link.platform, iconSize)
              ) : (
                // SVG icons need a colored circle background
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    backgroundColor: platform.color,
                    color: "#ffffff",
                  }}
                >
                  {renderSocialIcon(link.platform, iconSize)}
                </div>
              )}
            </a>
            {showLabels && <span style={{ fontSize: "0.75rem", color: "#4b5563" }}>{platform.label}</span>}
          </div>
        );
      })}
    </div>
  );
};
